import type { PowerStatus } from '@dia/db';
import { readingOf, type Reading } from './power';

/**
 * Where the power is coming from and where it is going, right now.
 *
 * Pure, so the same answer is on a phone with no signal and in a test. Plain
 * names only: Sun, House, Battery, Generator. Never "grid" or "consumption".
 *
 * Every figure here is a reading the island sent, not an estimate. The one
 * derived number is the "lost in conversion" line, which is what went in minus
 * what came out, and it is withheld whenever any piece of that sum is missing.
 *
 * Nothing is shown from a reading older than the fifteen minute line (Section
 * 8): a flow built from old numbers is a made up flow.
 */

/** Below this a flow is noise: the arrow is not drawn and the sentence does not claim it. */
const MEANINGFUL_W = 200;
/** Below this, "lost in conversion" is rounding, not a finding. */
const LOST_SHOWN_W = 100;
/** The sun is "down" under this. Panels give a few watts at night. */
const SUN_DOWN_W = 100;

/** The set point where the generator stops by itself, from Tim (starts at 10, stops at 65). */
export const GEN_STOP_SOC = 65;

export type FlowHeadline =
  | 'gen_charging'
  | 'gen_house'
  | 'battery_house'
  | 'sun_extra'
  | 'sun_plus_battery'
  | 'sun_house';

export type Flow = {
  headline: FlowHeadline;
  /** The box drawn at the top, the one everything else is fed from. */
  top: 'sun' | 'generator' | 'battery';
  /** Watts of the top box, or null when it is on but its figure is not known. */
  topW: number | null;
  sunW: number;
  /** Generator watts at the last reading, only while it is running. Null when running with no figure. */
  genW: number | null;
  houseW: number;
  /** Positive charging, negative draining, null when the island did not say. */
  batteryW: number | null;
  /** The arrow down to the house. */
  toHouseW: number;
  /** The arrow down to the battery. Null when no charge is flowing. */
  toBatteryW: number | null;
  /** Sun that is helping while the generator runs. Null when the sun adds nothing worth saying. */
  extraSunW: number | null;
  /** Battery watts helping the house while the sun is up. Null when it is not. */
  batteryToHouseW: number | null;
  /** What was lost between going in and coming out, or null when it cannot be worked out or is tiny. */
  lostW: number | null;
};

export type FlowInput = {
  s: PowerStatus;
  /** True when the generator state is running (already judged for age by the caller). */
  genRunning: boolean;
  nowMs: number;
  skewMs: number;
};

export type FlowResult =
  | { ok: true; flow: Flow }
  | { ok: false; why: 'no_reading' | 'stale' | 'silent'; solar: Reading; load: Reading };

export function flowOf({ s, genRunning, nowMs, skewMs }: FlowInput): FlowResult {
  const solar = readingOf(s.solar.nowW, s.solar.ts, nowMs, skewMs);
  const load = readingOf(s.load.nowW, s.load.ts, nowMs, skewMs);
  const worst = [solar, load].find((r) => r.level !== 'fresh');
  if (worst || solar.value === null || load.value === null) {
    const why = worst ? (worst.level === 'none' ? 'no_reading' : (worst.level as 'stale' | 'silent')) : 'no_reading';
    return { ok: false, why, solar, load };
  }

  const sunW = Math.max(0, solar.value);
  const houseW = Math.max(0, load.value);

  // Battery power rides on the battery's own timestamp. Old, it is unknown.
  const batt = readingOf(s.battery.powerW, s.battery.ts, nowMs, skewMs);
  const batteryW = batt.level === 'fresh' ? batt.value : null;
  const charge = batteryW !== null && batteryW > 0 ? batteryW : 0;
  const drain = batteryW !== null && batteryW < 0 ? -batteryW : 0;

  const genNow = s.generator.nowW;
  const genW = genRunning ? (typeof genNow === 'number' && genNow > 0 ? genNow : null) : 0;

  let top: Flow['top'];
  if (genRunning) top = 'generator';
  else if (sunW < SUN_DOWN_W && drain >= 0 && batteryW !== null) top = 'battery';
  else top = 'sun';

  const lostRaw =
    batteryW !== null && genW !== null ? sunW + genW + drain - houseW - charge : null;
  const lostW = lostRaw !== null && lostRaw >= LOST_SHOWN_W ? Math.round(lostRaw) : null;

  let headline: FlowHeadline;
  if (top === 'generator') headline = charge >= MEANINGFUL_W ? 'gen_charging' : 'gen_house';
  else if (top === 'battery') headline = 'battery_house';
  else if (charge >= MEANINGFUL_W) headline = 'sun_extra';
  else if (drain >= MEANINGFUL_W) headline = 'sun_plus_battery';
  else headline = 'sun_house';

  return {
    ok: true,
    flow: {
      headline,
      top,
      topW: top === 'generator' ? genW : top === 'battery' ? drain : sunW,
      sunW: Math.round(sunW),
      genW,
      houseW: Math.round(houseW),
      batteryW: batteryW === null ? null : Math.round(batteryW),
      toHouseW: Math.round(houseW),
      toBatteryW: charge >= MEANINGFUL_W ? Math.round(charge) : null,
      extraSunW: top === 'generator' && sunW >= MEANINGFUL_W ? Math.round(sunW) : null,
      batteryToHouseW: top === 'sun' && drain >= MEANINGFUL_W ? Math.round(drain) : null,
      lostW,
    },
  };
}

/**
 * How thick an arrow is, in pixels, for a given power. Proportional on a shared
 * scale so two arrows can be compared by eye, with a floor so a small flow is
 * still visible and a ceiling so the biggest still fits a phone.
 */
export function arrowWidth(watts: number, scaleW: number): number {
  if (!(scaleW > 0)) return 4;
  return Math.round(Math.max(4, Math.min(28, 4 + (watts / scaleW) * 24)));
}

/** The shared scale for the arrows on one screen: the biggest thing moving, never under 3 kW. */
export function arrowScale(f: Flow): number {
  return Math.max(3000, f.toHouseW, f.toBatteryW ?? 0);
}

/* ------------------------------------------------------------------ */
/* Tanks                                                               */
/* ------------------------------------------------------------------ */

/** A tank at a quarter or less is low, and at nothing it is empty. The word travels with the colour. */
export function waterWord(quarters: number): 'empty' | 'low' | 'ok' {
  return quarters <= 0 ? 'empty' : quarters === 1 ? 'low' : 'ok';
}
